import {
  PhoneOutlined,
  BarChartOutlined,
  PhoneFilled,
  InfoCircleOutlined,
  PictureOutlined,
  FileTextOutlined,
  StarOutlined,
 } from "@ant-design/icons";
import { GBP } from "../../types/Listing";
import React, { memo } from "react";

interface ListingCardProps {
  gbp: GBP;
  days?: number;
  userIsAdmin?: boolean;
   onDetails?: (g: GBP) => void;
  onOpenReviews?: (g: GBP) => void;
  onAssign?: (g: GBP) => void;
  onPerformance?: (g: GBP) => void;
  onPhotos?: (g: GBP) => void;
  onPosts?: (g: GBP) => void;
  title?: string;
  subtitle?: string;
  phone?: string;
  rating?: number;
  reviewCount?: number;
  lastSync?: string;
  error?: string;
  headerExtra?: React.ReactNode;
  actions?: React.ReactNode;
}

 const StatusBadges: React.FC<{ status?: GBP["status"] }> = ({ status }) => {
  if (!status) return null;

  const badges: { show: boolean; label: string; dot: string; cls: string }[] = [
    {
      show: status.suspended,
      label: "Suspendu",
      dot: "bg-rose-400",
      cls: "text-rose-700 bg-rose-50 ring-1 ring-rose-200",
    },
    {
      show: status.needsReverification,
      label: "Revérification",
      dot: "bg-amber-400",
      cls: "text-amber-700 bg-amber-50 ring-1 ring-amber-200",
    },
    {
      show: !status.suspended && !status.published,
      label: "Non publié",
      dot: "bg-slate-400",
      cls: "text-slate-600 bg-slate-50 ring-1 ring-slate-200",
    },
    {
      show: !status.suspended && status.published && status.verified,
      label: "Actif",
      dot: "bg-emerald-400",
      cls: "text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200",
    },
    {
      show: !status.suspended && status.published && !status.verified,
      label: "Non vérifié",
      dot: "bg-orange-400",
      cls: "text-orange-700 bg-orange-50 ring-1 ring-orange-200",
    },
  ];

   const active = badges.find((b) => b.show);
  if (!active) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap flex-shrink-0 ${active.cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${active.dot}`} />
      {active.label}
    </span>
  );
};

 const CallClicksBadge: React.FC<{ value: number | null | undefined; days?: number }> = ({
  value,
  days = 30,
}) => {
  if (value === null || value === undefined) return null;
  const isHigh   = value >= 10;
  const isMedium = value >= 3;
  const color = isHigh ? "text-emerald-600 bg-emerald-50 ring-emerald-200"
              : isMedium ? "text-amber-600 bg-amber-50 ring-amber-200"
              : "text-slate-500 bg-slate-50 ring-slate-200";

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${color}`}>
      <PhoneFilled style={{ fontSize: 9 }} />
      {value} appel{value !== 1 ? "s" : ""}{" "}
      <span className="font-normal opacity-60">/ {days}j</span>
    </span>
  );
};

 const StarRating: React.FC<{ rating: number; count: number }> = ({ rating, count }) => {
  const pct = Math.round((rating / 5) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-amber-400"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[12px] font-semibold text-amber-600">{rating.toFixed(1)}</span>
      <span className="text-[11px] text-slate-400">({count} avis)</span>
    </div>
  );
};

 const ActionBtn: React.FC<{
  title: string;
  onClick: () => void;
  icon: React.ReactNode;
  variant?: "blue" | "orange" | "ghost";
}> = ({ title, onClick, icon, variant = "blue" }) => {
  const cls = {
    blue:   "bg-blue-50 text-[#0F52BA] hover:bg-blue-100 hover:text-blue-800",
    orange: "bg-orange-50 text-orange-500 hover:bg-orange-100 hover:text-orange-700",
    ghost:  "bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700",
  }[variant];
  return (
    <button
      title={title}
      onClick={onClick}
      className={`h-7 w-7 flex items-center justify-center rounded-lg text-[13px] transition-colors duration-150 ${cls}`}
    >
      {icon}
    </button>
  );
};

 const ListingCard = memo<ListingCardProps>(({
  gbp, onDetails, onOpenReviews, onAssign, onPerformance, onPhotos, onPosts, days,
  userIsAdmin,
  title, subtitle, phone,
  rating, reviewCount, lastSync, headerExtra, actions,
}) => {
  const displayTitle    = title    || gbp.name            || "—";
  const displaySubtitle = subtitle || gbp.primaryCategory?.displayName || "—";
  const displayPhone    = phone    || gbp.phone            || "—";

  return (
    <div className="group relative bg-white rounded-2xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_20px_rgba(15,82,186,0.10)] hover:-translate-y-0.5 transition-all duration-200 flex flex-col overflow-hidden">

       <div className="h-0.5 w-full bg-gradient-to-r from-[#0F52BA] via-blue-400 to-blue-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

       <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-3">
         <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <h3 className="text-[14px] font-bold text-slate-800 leading-tight truncate">
            {displayTitle}
          </h3>
          <p className="text-[11px] text-orange-400 truncate">{displaySubtitle}</p>
          {headerExtra}
        </div>

        <StatusBadges status={gbp.status} />

      </div>

       <div className="px-4 pb-4 flex flex-col gap-3 flex-grow">

         <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
            <PhoneOutlined className="text-slate-300" style={{ fontSize: 12 }} />
            <span className="font-mono">{displayPhone}</span>
          </div>
          <CallClicksBadge value={gbp.callClicks} days={days} />
        </div>

         {rating !== undefined && (
          <StarRating rating={rating} count={reviewCount ?? 0} />
        )}


         {lastSync && (
          <p className="text-[10px] text-slate-300">Sync : {lastSync}</p>
        )}
      </div>

       <div className="mx-4 border-t border-slate-50" />

       <div className="px-4 py-3 flex items-center justify-between gap-2">

         <div className="flex items-center gap-1">
          {onDetails && (
            <ActionBtn title="Détails" onClick={() => onDetails(gbp)} icon={<InfoCircleOutlined />} />
          )}
          {onOpenReviews && (
            <ActionBtn title="Avis" onClick={() => onOpenReviews(gbp)} icon={<StarOutlined />} variant="orange" />
          )}
          {onPerformance && (
            <ActionBtn title="Performances" onClick={() => onPerformance(gbp)} icon={<BarChartOutlined />} />
          )}
          <ActionBtn title="Photos" onClick={() => onPhotos?.(gbp)} icon={<PictureOutlined />} variant="ghost" />
          <ActionBtn title="Posts"  onClick={() => onPosts?.(gbp)}  icon={<FileTextOutlined />} variant="ghost" />
          {actions}
        </div>


      </div>
    </div>
  );
});

ListingCard.displayName = "ListingCard";
export default ListingCard;